import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import ReelPlayer from "../components/ReelPlayer";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import CrossCreatePrompt from "../components/CrossCreatePrompt";
import {
  Utensils, Dumbbell, ShoppingBag, Briefcase, Home as HomeIcon,
  Calendar, User, Scissors, Bed, Car, Cpu, Stethoscope,
  ChevronLeft, ChevronRight, Sparkles, Loader2, Check, Building2, X, Camera, Image as ImageIcon, Video, Trash2,
} from "lucide-react";

const iconMap = {
  "utensils": Utensils, "dumbbell": Dumbbell, "shopping-bag": ShoppingBag,
  "briefcase": Briefcase, "home": HomeIcon, "calendar": Calendar,
  "user": User, "scissors": Scissors, "bed": Bed, "car": Car,
  "cpu": Cpu, "stethoscope": Stethoscope,
};

const TONES = [
  { id: "energetic", label: "Energico" },
  { id: "elegant", label: "Elegante" },
  { id: "playful", label: "Giocoso" },
  { id: "professional", label: "Professionale" },
  { id: "bold", label: "Audace" },
];

const LANGS = [
  { id: "it", label: "Italiano" },
  { id: "en", label: "English" },
  { id: "es", label: "Español" },
  { id: "fr", label: "Français" },
];

const ReelCreator = () => {
  const { user } = useAuth();
  const isAgency = user?.plan === "agency";
  const [step, setStep] = useState(1);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [category, setCategory] = useState(null);
  const [business, setBusiness] = useState({
    business_name: "",
    description: "",
    offer: "",
    target_audience: "",
    tone: "energetic",
    language: "it",
    topic: "",
  });
  const [numScenes, setNumScenes] = useState(4);
  const [customImages, setCustomImages] = useState([]); // [{label, image_base64}]
  const [customVideos, setCustomVideos] = useState([]); // [{video_id, label, duration_ms, size}]
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [useBrandHero, setUseBrandHero] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [reel, setReel] = useState(null);
  // Carousel-specific UI state. `contentFormat` can be:
  //   "reel"     → only the video reel (legacy behavior, default)
  //   "carousel" → only the slide carousel (new format)
  const [contentFormat, setContentFormat] = useState("reel");
  const [carouselTemplate, setCarouselTemplate] = useState("educational");
  const [numSlides, setNumSlides] = useState(6);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data.categories)).catch(() => {});
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
  }, []);

  // Whenever we transition between steps, scroll back to the top of the
  // page so the user always sees the section header first. Without this,
  // clicking "Continua" from step 1 leaves the page scrolled half-way
  // down and the "Cosa vuoi creare?" picker is hidden below the fold.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [step]);

  // For Free/Pro users: a single brand profile is the only way to create
  // reels. If the user already has a brand, auto-select it and skip the
  // category-picker step entirely (we already know the industry from the
  // brand). Agency users keep the manual brand-or-category choice flow.
  useEffect(() => {
    if (isAgency) return;
    if (!brands.length || !categories.length) return;
    if (selectedBrand) return;
    // Auto-pick the first (and on Free/Pro, only) brand and pre-fill business
    const brand = brands[0];
    const cat = categories.find((c) => c.id === brand.industry) || null;
    setSelectedBrand(brand);
    setCategory(cat);
    setBusiness((b) => ({
      ...b,
      business_name: brand.name,
      description: brand.description,
      target_audience: brand.target_audience || "",
      tone: brand.tone || b.tone || "energetic",
    }));
    // Jump straight to the "scene + tone" step. Step 1 (category) and
    // step 2 (business info) are pre-filled from the brand and not shown.
    setStep(2);
  }, [brands, categories, isAgency, selectedBrand]);


  const pickBrand = (brand) => {
    setSelectedBrand(brand);
    const cat = categories.find((c) => c.id === brand.industry);
    setCategory(cat || null);
    setBusiness((b) => ({
      ...b,
      business_name: brand.name,
      description: brand.description,
      target_audience: brand.target_audience || "",
      tone: brand.tone || "energetic",
      offer: b.offer || "",
      topic: b.topic || "",
    }));
  };

  const clearBrand = () => {
    setSelectedBrand(null);
  };

  const handleGenerate = async () => {
    if (!selectedBrand) {
      if (!category) return toast.error("Seleziona una categoria");
      if (!business.business_name.trim()) return toast.error("Inserisci il nome del business");
      if (!business.description.trim()) return toast.error("Inserisci una descrizione");
    }
    // Carosello richiede un argomento esplicito (è il filo conduttore di tutto)
    if (contentFormat === "carousel" && !(business.topic || "").trim()) {
      return toast.error("Per il carosello l'argomento è obbligatorio");
    }

    setGenerating(true);
    setStep(3);
    try {
      // ---- CAROUSEL-ONLY PATH ----
      if (contentFormat === "carousel") {
        const carouselPayload = selectedBrand
          ? {
              brand_id: selectedBrand.id,
              topic: business.topic,
              offer: business.offer,
              num_slides: numSlides,
              template: carouselTemplate,
            }
          : {
              category: category.id,
              business,
              topic: business.topic,
              num_slides: numSlides,
              template: carouselTemplate,
            };
        // Use the async-job endpoint to bypass Cloudflare's 60-100s edge
        // timeout. Production sometimes sees Gemini calls that exceed the
        // gateway window — polling decouples request lifetime from work.
        const { data: job } = await api.post("/carousels/generate-jobs", carouselPayload);
        const jobId = job.job_id;
        const startedAt = Date.now();
        const MAX_BUDGET_MS = 6 * 60 * 1000;
        let consecutiveFailures = 0;
        let finalCarouselId = null;
        while (Date.now() - startedAt < MAX_BUDGET_MS) {
          const elapsed = Date.now() - startedAt;
          const wait = elapsed < 30_000 ? 2000 : elapsed < 90_000 ? 4000 : 6000;
          await new Promise((r) => setTimeout(r, wait));
          try {
            const { data: jobStatus } = await api.get(`/carousels/jobs/${jobId}`);
            consecutiveFailures = 0;
            if (jobStatus.status === "done") {
              finalCarouselId = jobStatus.carousel_id;
              break;
            }
            if (jobStatus.status === "error") {
              throw new Error(jobStatus.error || "Errore generazione carosello");
            }
          } catch (pollErr) {
            consecutiveFailures += 1;
            if (consecutiveFailures > 8) throw pollErr;
          }
        }
        if (!finalCarouselId) {
          throw new Error("Generazione carosello troppo lenta — il file potrebbe essere pronto, controlla la dashboard tra qualche secondo");
        }
        toast.success("Carosello generato!");
        // Hint CarouselDetail to pop the "create matching reel?" modal once
        try { sessionStorage.setItem("cross_create:just_arrived", "1"); } catch (_) {}
        navigate(`/carousel/${finalCarouselId}`);
        return;
      }

      // ---- REEL or BOTH ----
      const payload = selectedBrand
        ? {
            category: selectedBrand.industry,
            brand_id: selectedBrand.id,
            offer: business.offer,
            topic: business.topic,
            business: { ...business },
            num_scenes: numScenes,
            use_brand_hero: useBrandHero,
            custom_images: customImages.map((c) => c.image_base64),
            custom_image_labels: customImages.map((c) => c.label),
            custom_videos: customVideos.map((v) => ({ video_id: v.video_id, label: v.label, duration_ms: v.duration_ms })),
          }
        : {
            category: category.id,
            business,
            topic: business.topic,
            num_scenes: numScenes,
            use_brand_hero: false,
            custom_images: customImages.map((c) => c.image_base64),
            custom_image_labels: customImages.map((c) => c.label),
            custom_videos: customVideos.map((v) => ({ video_id: v.video_id, label: v.label, duration_ms: v.duration_ms })),
          };

      const { data: job } = await api.post("/reels/generate", payload);
      const jobId = job.job_id;

      const pollStart = Date.now();
      const pollTimeout = 5 * 60 * 1000;
      let result = null;
      let consecutiveErrors = 0;
      let lastStatus = "queued";

      while (Date.now() - pollStart < pollTimeout) {
        await new Promise((r) => setTimeout(r, 4000));
        try {
          const { data: status } = await api.get(`/reels/jobs/${jobId}`);
          consecutiveErrors = 0;
          lastStatus = status.status || lastStatus;
          if (status.status === "done" && status.reel_id) {
            // Fetch the full reel via a separate small-payload-friendly call
            const { data: reelData } = await api.get(`/reels/${status.reel_id}`);
            result = reelData;
            break;
          }
          if (status.status === "failed") {
            throw new Error(status.error || "Generazione fallita");
          }
        } catch (pollErr) {
          const code = pollErr?.response?.status;
          // Tolerate transient gateway errors for a long time during long generations
          if (code === 502 || code === 503 || code === 504 || !code) {
            consecutiveErrors += 1;
            if (consecutiveErrors >= 15) {
              // Give up on polling but try to recover: check if the reel now exists in user's library
              try {
                const { data: list } = await api.get("/reels");
                const match = list.find((r) => r.created_at > new Date(pollStart).toISOString());
                if (match) {
                  toast.success("Reel generato! Reindirizzo...");
                  navigate(`/reel/${match.id}`);
                  return;
                }
              } catch (_e) { /* ignore */ }
              throw new Error("Il server ha avuto problemi. Controlla la tua dashboard, il reel potrebbe esserci già.");
            }
            continue;
          }
          throw pollErr;
        }
      }

      if (!result) {
        // Last-chance recovery
        try {
          const { data: list } = await api.get("/reels");
          const match = list.find((r) => r.created_at > new Date(pollStart).toISOString());
          if (match) {
            toast.success("Reel generato!");
            navigate(`/reel/${match.id}`);
            return;
          }
        } catch (_e) { /* ignore */ }
        throw new Error("Timeout. Controlla la dashboard, il reel potrebbe essere pronto.");
      }
      setReel(result);
      toast.success("Reel generato!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Errore nella generazione");
      setStep(2);
    } finally {
      setGenerating(false);
    }
  };

  const STEPS = [
    { n: 1, label: "Categoria" },
    { n: 2, label: "Business" },
    { n: 3, label: "Reel" },
  ];

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        {/* Stepper */}
        <div className="flex items-center gap-6 mb-10 flex-wrap">
          {STEPS.map((s, i) => {
            const done = step > s.n;
            const active = step === s.n;
            return (
              <div key={s.n} className="flex items-center gap-3">
                <div
                  className={`w-8 h-8 rounded-sm flex items-center justify-center border text-xs font-bold font-display ${
                    active
                      ? "bg-[#FF4400] border-[#FF4400] text-white"
                      : done
                      ? "bg-[#FF4400]/10 border-[#FF4400] text-[#FF4400]"
                      : "border-zinc-800 text-zinc-600"
                  }`}
                >
                  {done ? <Check className="w-4 h-4" /> : s.n}
                </div>
                <div
                  className={`font-mono text-[11px] uppercase tracking-widest ${
                    active ? "text-white" : done ? "text-[#FF4400]" : "text-zinc-600"
                  }`}
                >
                  {s.label}
                </div>
                {i < STEPS.length - 1 && <div className="w-8 h-px bg-zinc-800" />}
              </div>
            );
          })}
        </div>

        {/* STEP 1: Category or Brand */}
        {step === 1 && (
          <div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-white mb-2">
              Scegli da dove partire.
            </h1>
            <p className="text-zinc-400 font-body mb-8">
              Usa un brand profile salvato o crea un reel ad-hoc.
            </p>

            {/* Brand quick-select */}
            {brands.length > 0 && (
              <div className="mb-10">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-4 h-4 text-[#FF4400]" />
                  <h2 className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#FF4400]">
                    I tuoi brand profiles
                  </h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {brands.map((b) => {
                    const selected = selectedBrand?.id === b.id;
                    return (
                      <button
                        key={b.id}
                        data-testid={`brand-quickpick-${b.id}`}
                        onClick={() => (selected ? clearBrand() : pickBrand(b))}
                        className={`text-left border p-4 rounded-sm transition-all duration-300 ${
                          selected
                            ? "border-[#FF4400] bg-[#FF4400]/10 -translate-y-1"
                            : "border-zinc-800 bg-zinc-950 hover:border-zinc-700 hover:-translate-y-1"
                        }`}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div
                            className="w-8 h-8 rounded-sm flex items-center justify-center font-display font-black text-white text-xs overflow-hidden"
                            style={{ backgroundColor: b.color_primary || "#FF4400" }}
                          >
                            {b.logo_base64 ? (
                              <img src={`data:image/png;base64,${b.logo_base64}`} alt={b.name} className="w-full h-full object-cover" />
                            ) : (
                              b.name?.[0]?.toUpperCase() || "B"
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-display font-bold text-white text-sm truncate">{b.name}</div>
                            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 truncate">
                              {b.industry}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-zinc-500" />
              <h2 className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                {selectedBrand ? "Oppure cambia categoria" : "Scegli una categoria"}
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-10">
              {categories.map((c) => {
                const Icon = iconMap[c.icon] || Sparkles;
                const selected = category?.id === c.id;
                return (
                  <button
                    key={c.id}
                    data-testid={`category-select-${c.id}`}
                    onClick={() => { setCategory(c); setSelectedBrand(null); }}
                    className={`text-left border p-5 rounded-sm transition-all duration-300 group ${
                      selected
                        ? "border-[#FF4400] bg-[#FF4400]/10 -translate-y-1"
                        : "border-zinc-800 bg-zinc-950 hover:border-zinc-700 hover:-translate-y-1"
                    }`}
                  >
                    <Icon className={`w-5 h-5 mb-8 transition-colors ${selected ? "text-[#FF4400]" : "text-zinc-500 group-hover:text-white"}`} />
                    <div className="font-display font-bold text-white text-sm mb-1">{c.name}</div>
                    <div className="text-[11px] text-zinc-500 font-body leading-snug">{c.description}</div>
                  </button>
                );
              })}
            </div>

            <div className="flex justify-end">
              <button
                data-testid="step1-next"
                onClick={() => (category || selectedBrand) && setStep(2)}
                disabled={!category && !selectedBrand}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF4400] hover:bg-[#FF6633] disabled:opacity-40 disabled:cursor-not-allowed text-white font-display font-bold rounded-sm transition-colors"
              >
                Continua
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Business info */}
        {step === 2 && (
          <div className="max-w-3xl">
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-white mb-2">
              {selectedBrand ? "Dettagli del contenuto." : "Racconta il tuo brand."}
            </h1>
            <p className="text-zinc-400 font-body mb-10">
              {selectedBrand ? (
                <>Contenuto per <span className="text-[#FF4400]">{selectedBrand.name}</span>. Personalizza l'argomento e i media qui sotto.</>
              ) : (
                <>Più dettagli dai, migliore sarà il contenuto. <span className="text-[#FF4400]">{category?.name}</span></>
              )}
            </p>

            <div className="space-y-6">
              {/* Format picker: REEL / CAROUSEL / BOTH */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-zinc-500" />
                  <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    Cosa vuoi creare?
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3" data-testid="content-format-picker">
                  {[
                    { id: "reel", title: "Reel", subtitle: "Video 9:16", icon: Video },
                    { id: "carousel", title: "Carosello", subtitle: "Slide 4:5", icon: ImageIcon },
                  ].map((opt) => {
                    const Icon = opt.icon;
                    const sel = contentFormat === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        data-testid={`format-${opt.id}`}
                        onClick={() => setContentFormat(opt.id)}
                        className={`flex flex-col items-center text-center gap-1 p-4 border rounded-sm transition-all duration-300 ${
                          sel
                            ? "border-[#FF4400] bg-[#FF4400]/10 -translate-y-1"
                            : "border-zinc-800 bg-zinc-950 hover:border-zinc-700 hover:-translate-y-1"
                        }`}
                      >
                        <Icon className={`w-5 h-5 mb-1 ${sel ? "text-[#FF4400]" : "text-zinc-500"}`} />
                        <div className={`font-display font-black text-base tracking-tight ${sel ? "text-white" : "text-zinc-300"}`}>
                          {opt.title}
                        </div>
                        <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                          {opt.subtitle}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Topic field - visible for BOTH brand and ad-hoc flows */}
              <div className="border border-[#FF4400]/40 bg-[#FF4400]/5 rounded-sm p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-[#FF4400]" />
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-[#FF4400]">
                    {contentFormat === "carousel"
                      ? "Argomento del contenuto *"
                      : "Argomento del reel (opzionale)"}
                  </label>
                </div>
                <p className="text-sm text-zinc-400 font-body mb-3">
                  Vuoi un contenuto su un tema specifico? Scrivilo qui. L'AI userà sempre il tuo brand come contesto, ma il contenuto parlerà <span className="text-white font-bold">solo</span> di questo argomento.
                </p>
                <input
                  type="text"
                  data-testid="input-topic"
                  value={business.topic}
                  onChange={(e) => setBusiness({ ...business, topic: e.target.value })}
                  placeholder={selectedBrand?.industry === "services" || selectedBrand?.industry === "business" ? "Es. Surroga mutuo, come funziona e perché conviene" : "Es. Nuovo menù estivo, Black Friday, Open day..."}
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] rounded-sm font-body"
                />
                <div className="flex flex-wrap gap-1.5 mt-3">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 mr-1">suggerimenti:</span>
                  {["Surroga mutuo", "Nuova promozione", "Testimonianza cliente", "Dietro le quinte", "Errore comune da evitare", "FAQ rapide"].map((sug) => (
                    <button
                      key={sug}
                      type="button"
                      data-testid={`topic-suggestion-${sug.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setBusiness({ ...business, topic: sug })}
                      className="px-2 py-0.5 text-[10px] font-mono border border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-[#FF4400] hover:text-[#FF4400] rounded-sm transition-colors"
                    >
                      {sug}
                    </button>
                  ))}
                </div>
              </div>

              {/* Always-visible editable fields. Brand selection prefills them; user can override per-reel. */}
              {selectedBrand && !isAgency && (
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 -mb-2">
                  · Reel per <span className="text-[#FF4400]">{selectedBrand.name}</span>. Personalizza l'argomento e i media qui sotto.
                </div>
              )}
              {selectedBrand && isAgency && (
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 -mb-2">
                  · I campi qui sotto sono pre-compilati dal brand. Modificali per personalizzare solo questo reel (il brand resta invariato).
                </div>
              )}

              {/* Free/Pro single-brand users: hide business_name/description/target.
                  These are already locked to the brand profile — there's no
                  scenario where they'd want to override them per-reel. */}
              {(!selectedBrand || isAgency) && (
                <>
                  <Field label={selectedBrand ? "Nome del business (override per questo reel)" : "Nome del business *"}>
                    <input
                      type="text"
                      data-testid="input-business-name"
                      value={business.business_name}
                      onChange={(e) => setBusiness({ ...business, business_name: e.target.value })}
                      placeholder="Es. Trattoria Da Luigi"
                      className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] rounded-sm font-body"
                    />
                  </Field>

                  <Field label={selectedBrand ? "Descrizione (override per questo reel)" : "Descrizione *"}>
                    <textarea
                      data-testid="input-description"
                      rows={3}
                      value={business.description}
                      onChange={(e) => setBusiness({ ...business, description: e.target.value })}
                      placeholder="Cosa fai, cosa ti rende unico, da quanto tempo lo fai..."
                      className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] rounded-sm font-body resize-none"
                    />
                  </Field>

                  <Field label={selectedBrand ? "Target (override per questo reel)" : "Target"}>
                    <input
                      type="text"
                      data-testid="input-target"
                      value={business.target_audience}
                      onChange={(e) => setBusiness({ ...business, target_audience: e.target.value })}
                      placeholder="Es. Giovani 25-35 Milano"
                      className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] rounded-sm font-body"
                    />
                  </Field>
                </>
              )}

              <Field label={selectedBrand ? "Tono di voce (override per questo reel)" : "Tono di voce"}>
                <div className="flex flex-wrap gap-2">
                  {TONES.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      data-testid={`tone-${t.id}`}
                      onClick={() => setBusiness({ ...business, tone: t.id })}
                      className={`px-4 py-2 text-sm rounded-sm border transition-all font-body ${
                        business.tone === t.id
                          ? "bg-[#FF4400] border-[#FF4400] text-white font-bold"
                          : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700 hover:text-white"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Offerta / promo">
                <input
                  type="text"
                  data-testid="input-offer"
                  value={business.offer}
                  onChange={(e) => setBusiness({ ...business, offer: e.target.value })}
                  placeholder="Es. Sconto 20% primo ordine"
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] rounded-sm font-body"
                />
              </Field>

              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Lingua">
                  <div className="flex flex-wrap gap-2">
                    {LANGS.map((l) => (
                      <button
                        key={l.id}
                        type="button"
                        data-testid={`lang-${l.id}`}
                        onClick={() => setBusiness({ ...business, language: l.id })}
                        className={`px-3 py-2 text-sm rounded-sm border transition-all font-body ${
                          business.language === l.id
                            ? "bg-[#FF4400] border-[#FF4400] text-white font-bold"
                            : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700 hover:text-white"
                        }`}
                      >
                        {l.label}
                      </button>
                    ))}
                  </div>
                </Field>

                {/* Reel-specific: number of video scenes. Hidden for carousel-only. */}
                {contentFormat !== "carousel" && (
                  <Field label={`Numero scene (${numScenes})`}>
                    <div className="flex items-center gap-2">
                      {[3, 4, 5, 6, 7, 8, 9, 10, 12, 15].map((n) => (
                        <button
                          key={n}
                          type="button"
                          data-testid={`scenes-${n}`}
                          onClick={() => setNumScenes(n)}
                          className={`w-10 h-10 rounded-sm border font-display font-bold transition-all ${
                            numScenes === n
                              ? "bg-[#FF4400] border-[#FF4400] text-white"
                              : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700 hover:text-white"
                          }`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </Field>
                )}
              </div>

              {/* Carousel-only configuration: template + slide count.
                  Hidden when the user picked "Reel" (legacy flow). */}
              {contentFormat === "carousel" && (
                <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <ImageIcon className="w-4 h-4 text-[#FF4400]" />
                    <label className="block font-mono text-[10px] uppercase tracking-widest text-[#FF4400]">
                      Impostazioni Carosello
                    </label>
                  </div>

                  <Field label="Schema del carosello">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="carousel-template-picker">
                      {[
                        { id: "educational", title: "Educational", desc: "Hook + 5 errori/segreti + CTA" },
                        { id: "storytelling", title: "Storytelling", desc: "Problema → soluzione → morale" },
                        { id: "tips_list", title: "Tips List", desc: "Lista di consigli pratici" },
                      ].map((t) => {
                        const sel = carouselTemplate === t.id;
                        return (
                          <button
                            key={t.id}
                            type="button"
                            data-testid={`carousel-template-${t.id}`}
                            onClick={() => setCarouselTemplate(t.id)}
                            className={`text-left p-3 border rounded-sm transition-all ${
                              sel
                                ? "border-[#FF4400] bg-[#FF4400]/10"
                                : "border-zinc-800 bg-zinc-900 hover:border-zinc-700"
                            }`}
                          >
                            <div className={`font-display font-bold text-sm mb-1 ${sel ? "text-white" : "text-zinc-300"}`}>
                              {t.title}
                            </div>
                            <div className="font-body text-[11px] text-zinc-500 leading-tight">
                              {t.desc}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </Field>

                  <Field label={`Numero slide carosello (${numSlides})`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                        <button
                          key={n}
                          type="button"
                          data-testid={`carousel-slides-${n}`}
                          onClick={() => setNumSlides(n)}
                          className={`w-10 h-10 rounded-sm border font-display font-bold transition-all ${
                            numSlides === n
                              ? "bg-[#FF4400] border-[#FF4400] text-white"
                              : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700 hover:text-white"
                          }`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </Field>
                </div>
              )}

              {/* Custom images for this reel — reel-only feature */}
              {contentFormat !== "carousel" && (
              <>
              <div className="border border-[#FF4400]/40 bg-[#FF4400]/5 rounded-sm p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-[#FF4400]" />
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-[#FF4400]">
                    Tue foto reali (opzionale)
                  </label>
                </div>
                <p className="text-sm text-zinc-400 font-body mb-4">
                  Carica 1-4 tue foto (faccia, logo, ingresso locale, prodotto). Diventeranno scene reali del reel; l'AI adatta headline e sottotitolo a quello che mostri.
                </p>

                {selectedBrand?.hero_image && (
                  <label className="flex items-start gap-3 mb-4 cursor-pointer" data-testid="creator-use-hero-toggle">
                    <input
                      type="checkbox"
                      checked={useBrandHero}
                      onChange={(e) => setUseBrandHero(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-[#FF4400]"
                    />
                    <div className="flex gap-3 flex-1">
                      <img
                        src={`data:image/jpeg;base64,${selectedBrand.hero_image}`}
                        alt="Hero"
                        className="w-12 h-16 object-cover rounded-sm border border-zinc-800"
                      />
                      <div>
                        <div className="font-display font-bold text-white text-sm">
                          Usa la foto hero del brand
                        </div>
                        <div className="text-xs text-zinc-500 font-body">
                          {selectedBrand.hero_label || `Foto principale di ${selectedBrand.name}`} — sarà la scena 1
                        </div>
                      </div>
                    </div>
                  </label>
                )}

                <div className="grid grid-cols-4 gap-2 mb-3">
                  {customImages.map((ci, i) => (
                    <div key={i} className="relative group aspect-[9/16] border border-zinc-800 rounded-sm overflow-hidden bg-zinc-950" data-testid={`creator-custom-image-${i}`}>
                      <img src={`data:image/jpeg;base64,${ci.image_base64}`} alt={ci.label} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                      <button
                        type="button"
                        onClick={() => setCustomImages((arr) => arr.filter((_, idx) => idx !== i))}
                        className="absolute top-1 right-1 w-6 h-6 rounded-sm bg-black/80 border border-white/10 text-white hover:bg-[#FF4400] flex items-center justify-center"
                      >
                        <X className="w-3 h-3" />
                      </button>
                      <input
                        type="text"
                        value={ci.label}
                        onChange={(e) => setCustomImages((arr) => arr.map((c, idx) => idx === i ? {...c, label: e.target.value} : c))}
                        placeholder="etichetta"
                        className="absolute bottom-1 left-1 right-1 text-[10px] bg-black/60 backdrop-blur border border-white/10 text-white placeholder-zinc-500 rounded-sm px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#FF4400]"
                      />
                    </div>
                  ))}
                </div>
                {customImages.length < 4 && (
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <PhotoUploadButton
                      label="Galleria"
                      Icon={ImageIcon}
                      capture={false}
                      testId="creator-add-from-gallery"
                      onPicked={(items) => setCustomImages((arr) => [...arr, ...items].slice(0, 4))}
                    />
                    <PhotoUploadButton
                      label="Camera"
                      Icon={Camera}
                      capture={true}
                      testId="creator-add-from-camera"
                      onPicked={(items) => setCustomImages((arr) => [...arr, ...items].slice(0, 4))}
                    />
                  </div>
                )}
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">
                  {customImages.length + (selectedBrand?.hero_image && useBrandHero ? 1 : 0)} di {numScenes} scene personalizzate · resto generato da AI
                </div>
              </div>

              {/* Custom videos */}
              <div className="border border-[#FF4400]/40 bg-[#FF4400]/5 rounded-sm p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Video className="w-4 h-4 text-[#FF4400]" />
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-[#FF4400]">
                    Tuoi video reali (opzionale · max 3, max 30s)
                  </label>
                </div>
                <p className="text-sm text-zinc-400 font-body mb-4">
                  Aggiungi clip brevi (testimonianze, dietro le quinte, te che parli). Ogni video diventa una scena. Audio originale rimosso.
                </p>

                {customVideos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {customVideos.map((cv, i) => (
                      <div key={cv.video_id} className="relative aspect-[9/16] border border-zinc-800 rounded-sm overflow-hidden bg-black" data-testid={`creator-custom-video-${i}`}>
                        <video
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/reels/uploads/video/${cv.video_id}`}
                          muted
                          playsInline
                          autoPlay
                          loop
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                        <button
                          type="button"
                          onClick={() => setCustomVideos((arr) => arr.filter((_, idx) => idx !== i))}
                          className="absolute top-1 right-1 w-6 h-6 rounded-sm bg-black/80 border border-white/10 text-white hover:bg-[#FF4400] flex items-center justify-center z-10"
                          data-testid={`creator-video-remove-${i}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                        <div className="absolute bottom-1 left-1 right-1 text-[10px] font-mono uppercase tracking-widest text-white bg-black/60 backdrop-blur rounded-sm px-2 py-0.5">
                          {(cv.duration_ms / 1000).toFixed(1)}s · {cv.label}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {customVideos.length < 3 && (
                  <div className="grid grid-cols-2 gap-2">
                    <VideoUploadButton
                      label="Galleria"
                      Icon={Video}
                      capture={false}
                      testId="creator-add-video-gallery"
                      uploading={uploadingVideo}
                      setUploading={setUploadingVideo}
                      onUploaded={(v) => setCustomVideos((arr) => [...arr, v].slice(0, 3))}
                    />
                    <VideoUploadButton
                      label="Camera"
                      Icon={Camera}
                      capture={true}
                      testId="creator-add-video-camera"
                      uploading={uploadingVideo}
                      setUploading={setUploadingVideo}
                      onUploaded={(v) => setCustomVideos((arr) => [...arr, v].slice(0, 3))}
                    />
                  </div>
                )}
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 mt-2">
                  {customVideos.length} di 3 video · ognuno max 30s
                </div>
              </div>
              </>
              )}
            </div>

            <div className="flex justify-between items-center mt-10">
              {(!selectedBrand || isAgency) ? (
                <button
                  data-testid="step2-back"
                  onClick={() => setStep(1)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-zinc-400 hover:text-white rounded-sm transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Indietro
                </button>
              ) : (
                <button
                  data-testid="step2-edit-brand"
                  onClick={() => navigate(`/brands/${selectedBrand.id}`)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-zinc-400 hover:text-[#FF4400] rounded-sm transition-colors"
                >
                  <Building2 className="w-4 h-4" />
                  Modifica brand
                </button>
              )}
              <button
                data-testid="step2-generate"
                onClick={handleGenerate}
                disabled={generating}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF4400] hover:bg-[#FF6633] disabled:opacity-40 text-white font-display font-bold rounded-sm transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                {contentFormat === "carousel" ? "Genera Carosello" : "Genera Reel"}
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Result */}
        {step === 3 && (
          <div>
            {generating ? (
              <div className="flex flex-col items-center justify-center py-20" data-testid="generating-state">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full border-2 border-zinc-800 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 text-[#FF4400] animate-spin" />
                  </div>
                </div>
                <h2 className="font-display font-black text-3xl tracking-tighter text-white mt-8 mb-2">
                  Sto creando il tuo reel...
                </h2>
                <p className="text-zinc-400 font-body text-sm max-w-md text-center">
                  Generazione script → immagini AI → caption. Può richiedere 30-60 secondi.
                </p>
                <div className="mt-8 flex gap-2">
                  {[0, 1, 2, 3].map((i) => (
                    <span
                      key={i}
                      className="w-2 h-2 rounded-full bg-[#FF4400] pulse-dot"
                      style={{ animationDelay: `${i * 0.2}s` }}
                    />
                  ))}
                </div>
              </div>
            ) : reel ? (
              <div className="grid lg:grid-cols-5 gap-8">
                <div className="lg:col-span-2 flex justify-center items-start">
                  <ReelPlayer reel={reel} />
                </div>
                <div className="lg:col-span-3">
                  <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#FF4400] mb-3">
                    · Reel pronto
                  </div>
                  <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white mb-2" data-testid="result-title">
                    {reel.title}
                  </h1>
                  <p className="text-zinc-400 font-body mb-8">
                    <span className="text-[#FF4400]">Hook:</span> {reel.hook}
                  </p>

                  <div className="space-y-6">
                    <Section label="CTA">
                      <div className="text-white font-display font-bold">{reel.cta}</div>
                    </Section>
                    <Section label="Caption">
                      <pre className="whitespace-pre-wrap text-sm text-zinc-200 font-body" data-testid="result-caption">{reel.caption}</pre>
                    </Section>
                    <Section label="Hashtag">
                      <div className="flex flex-wrap gap-1.5">
                        {reel.hashtags.map((h) => (
                          <span key={h} className="px-2 py-1 text-xs font-mono text-[#FF4400] bg-[#FF4400]/10 border border-[#FF4400]/30 rounded-sm">
                            #{h}
                          </span>
                        ))}
                      </div>
                    </Section>
                    <Section label="Musica suggerita">
                      <div className="text-zinc-300 font-body">{reel.music_mood}</div>
                    </Section>
                  </div>

                  <div className="flex gap-3 mt-10">
                    <button
                      data-testid="result-view-detail"
                      onClick={() => navigate(`/reel/${reel.id}`)}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF4400] hover:bg-[#FF6633] text-white font-display font-bold rounded-sm transition-colors"
                    >
                      Vedi dettagli
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                      data-testid="result-new-reel"
                      onClick={() => {
                        setStep(1); setReel(null); setCategory(null); setSelectedBrand(null);
                        setCustomImages([]); setCustomVideos([]); setUseBrandHero(true);
                        setBusiness((b) => ({ ...b, topic: "", offer: "" }));
                      }}
                      className="inline-flex items-center gap-2 px-6 py-3 border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-900 text-white font-body font-medium rounded-sm transition-all"
                    >
                      Nuovo reel
                    </button>
                  </div>

                  {/* Cross-create: propose a matching carousel right after the reel is done */}
                  <div className="mt-10">
                    <CrossCreatePrompt
                      source={{
                        id: reel.id,
                        business_name: reel.business_name,
                        category: reel.category,
                        brand_id: reel.brand_id || selectedBrand?.id || null,
                        topic: business.topic || reel.title,
                        title: reel.title,
                        description: business.description,
                      }}
                      targetType="carousel"
                      mode="card"
                    />
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{label}</label>
    {children}
  </div>
);

// Compress an image File to a JPEG base64 (no data: prefix), max 1080x1920, quality 0.82
const compressImageFile = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const MAX_W = 1080, MAX_H = 1920;
      let { width, height } = img;
      const ratio = Math.min(MAX_W / width, MAX_H / height, 1);
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      // Try descending quality until under 1.4MB
      const tryAt = (q) => {
        const dataUrl = canvas.toDataURL("image/jpeg", q);
        const b64 = dataUrl.split(",")[1] || "";
        // base64 length * 0.75 ≈ bytes
        const approxBytes = b64.length * 0.75;
        if (approxBytes <= 1.4 * 1024 * 1024 || q <= 0.4) return b64;
        return tryAt(q - 0.1);
      };
      try {
        resolve(tryAt(0.82));
      } catch (e) { reject(e); }
    };
    img.onerror = () => reject(new Error("Immagine non leggibile"));
    img.src = String(reader.result);
  };
  reader.onerror = () => reject(new Error("Errore lettura file"));
  reader.readAsDataURL(file);
});

const PhotoUploadButton = ({ label, Icon, capture, testId, onPicked }) => (
  <label
    className="aspect-[9/16] md:aspect-auto md:py-4 border-2 border-dashed border-zinc-800 bg-zinc-950 rounded-sm flex flex-col items-center justify-center cursor-pointer hover:border-[#FF4400] text-zinc-400 hover:text-[#FF4400] transition-colors"
    data-testid={testId}
  >
    <Icon className="w-5 h-5 mb-1" />
    <span className="font-display font-bold text-sm">{label}</span>
    <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-600 mt-0.5">PNG / JPG</span>
    <input
      type="file"
      accept="image/*"
      multiple={!capture}
      {...(capture ? { capture: "environment" } : {})}
      className="hidden"
      onChange={async (e) => {
        const input = e.target;
        const files = Array.from(input.files || []);
        const out = [];
        for (const f of files) {
          try {
            const b64 = await compressImageFile(f);
            out.push({ image_base64: b64, label: f.name.replace(/\.[^.]+$/, "").slice(0, 24) || "foto" });
          } catch (err) {
            toast.error(`${f.name}: ${err.message}`);
          }
        }
        if (out.length) onPicked(out);
        try { if (input && input.isConnected) input.value = ""; } catch (_) {}
      }}
    />
  </label>
);

const VideoUploadButton = ({ label, Icon, capture, testId, onUploaded, uploading, setUploading }) => (
  <label
    className={`aspect-[9/16] md:aspect-auto md:py-4 border-2 border-dashed bg-zinc-950 rounded-sm flex flex-col items-center justify-center cursor-pointer transition-colors ${
      uploading ? "border-zinc-700 text-zinc-600 pointer-events-none opacity-60" : "border-zinc-800 hover:border-[#FF4400] text-zinc-400 hover:text-[#FF4400]"
    }`}
    data-testid={testId}
  >
    {uploading ? <Loader2 className="w-5 h-5 mb-1 animate-spin" /> : <Icon className="w-5 h-5 mb-1" />}
    <span className="font-display font-bold text-sm">{label}</span>
    <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-600 mt-0.5">MP4 / MOV · max 30s</span>
    <input
      type="file"
      accept="video/mp4,video/quicktime,video/webm,video/*"
      {...(capture ? { capture: "environment" } : {})}
      className="hidden"
      onChange={async (e) => {
        const input = e.target;
        const f = input.files?.[0];
        if (!f) return;
        // Pre-validate duration via HTMLVideoElement
        try {
          const url = URL.createObjectURL(f);
          const dur = await new Promise((res, rej) => {
            const v = document.createElement("video");
            v.preload = "metadata";
            v.onloadedmetadata = () => { res(v.duration); URL.revokeObjectURL(url); };
            v.onerror = () => rej(new Error("Video non leggibile"));
            v.src = url;
          });
          if (dur > 20.5) {
            toast.error(`Video troppo lungo (${dur.toFixed(1)}s) — max 30s`);
            try { if (input && input.isConnected) input.value = ""; } catch (_) {}
            return;
          }
          if (f.size > 60 * 1024 * 1024) {
            toast.error("Video troppo pesante (max 60MB)");
            return;
          }
        } catch (err) {
          toast.error(err.message || "Video non valido");
          return;
        }

        setUploading(true);
        try {
          const fd = new FormData();
          fd.append("file", f);
          fd.append("label", f.name.replace(/\.[^.]+$/, "").slice(0, 24) || "clip");
          const { data } = await api.post("/reels/uploads/video", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 120000,
          });
          onUploaded({
            video_id: data.video_id,
            duration_ms: data.duration_ms,
            size: data.size,
            label: f.name.replace(/\.[^.]+$/, "").slice(0, 24) || "clip",
          });
          toast.success(`Video caricato (${(data.duration_ms / 1000).toFixed(1)}s)`);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Errore caricamento video");
        } finally {
          setUploading(false);
          try { if (input && input.isConnected) input.value = ""; } catch (_) {}
        }
      }}
    />
  </label>
);

const Section = ({ label, children }) => (
  <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-4">
    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{label}</div>
    {children}
  </div>
);

export default ReelCreator;
